<template>
  <b-row cols="1" cols-sm="1" cols-md="1" cols-lg="1" cols-xl="1">
    <b-col>
      <div class="center-form">
        <h5>In order to deploy your app, please enter a valid payment method</h5>
        <br>
        Your free trial will end on {{ user.freeTrialEndAt | datetime }}. After this date, if you still have active deployments, the payment method entered below will be charged according to our <router-link :to="{ name: 'Pricing'}" target="_blank">pricing page</router-link>. If you stop or delete your deployments before the end of the free trial, you will not be charged anything.
        <br>
        <br>
        <form v-if="!done" id="payment-form">
          <div id="payment-element">
            <!-- Elements will create form elements here -->
          </div>
          <c-button :onClick="onSubmit" variant="primary">Submit</c-button>
          <div id="error-message">
            <!-- Display error message to your customers here -->
          </div>
        </form>
        <div id="message">
          <!-- Display success/error message to your customers here after retrieving setupIntent -->
        </div>
      </div>
    </b-col>
  </b-row>
</template>

<script>

  import CButton from '@/components/Button'
  import { onLogin } from '../vue-apollo';

  export default {
    name: 'SetupPayment',
    components: { CButton },
    props: {
      deploymentId: String
    },
    data() {
      return {
        failureMessage: '',
        stripe: null,
        elements: null,
        done: false
      }
    },
    async created() {
      this.stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY)

      if (this.$route.query.setup_intent_client_secret) {
        const deploymentId = this.$route.query.id ? this.$route.query.id : null
        await this.retrieveSetupIntent({ clientSecret: this.$route.query.setup_intent_client_secret, deploymentId })
      } else {
        await this.generateSetupIntent()
      }
    },
    computed: {
      user() {
        return this.$store.state.loggedUser;
      },
      environment() {
        return process.env.VUE_APP_MODE
      }
    },
    methods: {
      async generateSetupIntent() {
        this.failureMessage = ''

        const result = await this.$apollo.query({
          client: 'private',
          query: require('@/graphql/generateSetupIntent.graphql'),
          fetchPolicy: 'no-cache'
        });

        if (result.errors) {
          const e = result;
          if (e.body) {
            if (e.body.message) {
              this.failureMessage = e.body.message;
            }
          }
        }

        if (result && result.data && result.data.generateSetupIntent && result.data.generateSetupIntent.setupIntentClientSecret) {
          const setupIntentClientSecret = result.data.generateSetupIntent.setupIntentClientSecret

          const options = {
            clientSecret: setupIntentClientSecret,
            // Fully customizable with appearance API.
            appearance: {},
          };

          // Set up Stripe.js and Elements using the SetupIntent's client secret
          this.elements = this.stripe.elements(options);

          // Create and mount the Payment Element
          const paymentElement = this.elements.create('payment');
          paymentElement.mount('#payment-element');
        }
      },
      async onSubmit() {
        let returnUrl = `${process.env.VUE_APP_FRONT_URL}/account/setup-payment`
        if (this.deploymentId) returnUrl += `?id=${this.deploymentId}`

        const { error } = await this.stripe.confirmSetup({
          //`Elements` instance that was used to create the Payment Element
          elements: this.elements,
          confirmParams: {
            return_url: returnUrl,
          }
        });

        if (error) {
          // This point will only be reached if there is an immediate error when
          // confirming the payment. Show error to your customer (for example, payment
          // details incomplete)
          const messageContainer = document.querySelector('#error-message');
          messageContainer.textContent = error.message;
        }
      },
      async retrieveSetupIntent({ clientSecret, deploymentId }) {
        this.done = true

        // Retrieve the SetupIntent
        const { setupIntent } = await this.stripe.retrieveSetupIntent(clientSecret)

        const message = document.querySelector('#message')

        // Inspect the SetupIntent `status` to indicate the status of the payment
        // to your customer.
        //
        // Some payment methods will [immediately succeed or fail][0] upon
        // confirmation, while others will first enter a `processing` state.
        //
        // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
        switch (setupIntent.status) {
          case 'succeeded': {
            message.innerText = 'Success! Your payment method has been saved.';

            await this.setupIntentDone()

            this.sendGoogleAdsEvent()

            if (deploymentId) {
              this.$router.push({ name: 'Deployment', params: { id: deploymentId, start: true }});
            } else {
              this.$router.push({ name: 'Account' });
            }

            break;
          }

          case 'processing': {
            message.innerText = "Processing payment details. We'll update you when processing is complete.";

            await this.setupIntentDone()

            this.sendGoogleAdsEvent()

            if (deploymentId) {
              this.$router.push({ name: 'Deployment', params: { id: deploymentId, start: true }});
            } else {
              this.$router.push({ name: 'Account' });
            }

            break;
          }

          case 'requires_payment_method': {
            message.innerText = 'Failed to process payment details. Please try another payment method.';

            this.done = false

            await this.generateSetupIntent()

            break;
          }
        }
      },
      async setupIntentDone() {
        const result = await this.$apollo.mutate({
          client: 'private',
          mutation: require('@/graphql/setupIntentDone.graphql'),
          fetchPolicy: 'no-cache',
        });

        if (result.errors) {
          throw new Error('There has been an error sending setupIntentDone')
        }

        if (result && result.data && result.data.setupIntentDone && result.data.setupIntentDone.id) {
          const result = await this.$http.get(process.env.VUE_APP_API_BASE_URL + "/auth");
          if (result && result.body && result.body.user) {
            await onLogin.apply(this, [this.$apollo.provider.defaultClient, result.body.user])
          }
        }
      },
      sendGoogleAdsEvent() {
        if(this.environment === 'prod') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17674521674/My6MCLiYsrIbEMqY7-tB',
            'value': 1.0,
            'currency': 'EUR',
            'transaction_id': this.deploymentId
          })
        }
      }
    }
  }
</script>

<style scoped>

</style>
